import { ProgressBar, shapes } from '@cimpress/react-components';
import IconCheckCircleAlt from '@cimpress-technology/react-streamline-icons/lib/IconCheckCircleAlt';
import IconRemoveCircle from '@cimpress-technology/react-streamline-icons/lib/IconRemoveCircle';
import IconAlertTriangle from '@cimpress-technology/react-streamline-icons/lib/IconAlertTriangle';
import React from 'react';
import { FlexBox, FlexDirection, FlexGrow, JustifyContent } from '../../../utils/css-classes';
import { useCurrentLocation } from '../../../store/hooks/useCurrentLocation';
import momentTimezone from 'moment-timezone';
import { useModal } from '../../../contexts/modal-context';
import { UseWorkflowHook, UseWorkflowLogHook, WorkflowStatus, WorkflowSummary as WorkflowSummaryModel } from '../../../ports/WorkflowService';
import { createWorkflowDetailsModal } from '../WorkflowDetailsModal';
import { Location } from '../../../models/location';
import { useWorkflow } from '../../../adapters/use-workflow';
import { colors } from '../colors';
import { useWorkflowLog } from '../../../adapters/use-workflow-log';

// tslint:disable-next-line:import-name
import style from './index.module.css';

const { Spinner } = shapes;

const workflowDisplayNames: Record<string, string> = {
  'bulk-shipments-void': 'Bulk Shipments Void',
};

export function createWorkflowSummary(
  {
    useCurrentLocation,
    useWorkflow,
    useWorkflowLog,
  }: {
    useCurrentLocation: () => Location;
    useWorkflow: UseWorkflowHook;
    useWorkflowLog: UseWorkflowLogHook;
  },
) {
  // tslint:disable-next-line:variable-name
  const WorkflowDetailsModal = createWorkflowDetailsModal({ useWorkflow, useCurrentLocation, useWorkflowLog });

  // tslint:disable-next-line:function-name
  return function WorkflowSummary(
    {
      workflow,
    }: {
      workflow: WorkflowSummaryModel;
    },
  ) {
    const { localeSettings } = useCurrentLocation();
    const modal = useModal();
    return <div
      className={style.Workflow}
      onClick={() => modal(
        'Workflow details',
        () => <WorkflowDetailsModal workflowId={workflow.id}/>,
        {
          bsSize: 'lg',
        },
      )}
    >
      <div className={style.Icon}>
        {[WorkflowStatus.RUNNING, WorkflowStatus.CREATED].includes(workflow.status) && <Spinner size="medium" duration={3}/>}
        {workflow.status === WorkflowStatus.COMPLETED && <IconCheckCircleAlt color="var(--success)" size="3x"/>}
        {workflow.status === WorkflowStatus.FAILED && <IconRemoveCircle color="var(--danger)" size="3x"/>}
      </div>
      <div className={FlexGrow.GROW_1}>
        <h4>{workflowDisplayNames[workflow.name] ?? workflow.name}</h4>
        <div className={`${FlexBox.FLEX} ${FlexDirection.ROW} ${JustifyContent.SPACE_BETWEEN}`}>
          <span>
            {workflow.progress?.percentage ?? 0}% - <i>{workflow.progress?.summary ?? 'Starting'}</i>
          </span>
          <span className={style.Issues}>
            {workflow.hasWarnings && <i><IconAlertTriangle color="var(--warning)" size="1x" weight="fill" /> The workflow raised warnings</i>}
            {workflow.hasErrors && <i><IconRemoveCircle color="var(--danger)" size="1x" weight="fill" /> The workflow raised errors</i>}
          </span>
        </div>
        <ProgressBar
          color={`var(--${colors[workflow.status]})`}
          size="l"
          value={workflow.progress?.percentage ?? 0}
        />
        Created {momentTimezone(workflow.createdAt).tz(localeSettings.timezone).fromNow()}
        {
          workflow.status === WorkflowStatus.COMPLETED &&
          <> - Completed in {
            momentTimezone
              .duration(momentTimezone(workflow.updatedAt).diff(momentTimezone(workflow.createdAt)))
              .humanize()
          }</>
        }
        {
          workflow.status === WorkflowStatus.FAILED &&
          <> - Failed after {
            momentTimezone
              .duration(momentTimezone(workflow.updatedAt).diff(momentTimezone(workflow.createdAt)))
              .humanize()
          }</>
        }
        {
          [WorkflowStatus.RUNNING].includes(workflow.status) &&
          <> - Last updated {momentTimezone(workflow.updatedAt).tz(localeSettings.timezone).fromNow()}</>
        }
      </div>
    </div>;
  };
}

// tslint:disable-next-line:variable-name
export const WorkflowSummary = createWorkflowSummary({ useCurrentLocation, useWorkflow, useWorkflowLog });
